import type { ComicDetails, PageRef } from '@/sources/types';

/** How pages are laid out — chosen by the user or auto-defaulted by viewport. */
export type ReaderMode = 'vertical' | 'horizontal' | 'spread';

/** Image fit policy. `free` means "no constraint; user pans/zooms manually". */
export type ReaderFit = 'width' | 'height' | 'free';

/** Reading direction for spread/horizontal modes. */
export type ReadDirection = 'ltr' | 'rtl';

export interface ReaderPrefs {
  mode: ReaderMode;
  fit: ReaderFit;
  direction: ReadDirection;
  /** How many pages ahead to preload via `new Image()`. */
  preload: number;
}

export const DEFAULT_PREFS: ReaderPrefs = {
  mode: 'vertical',
  fit: 'width',
  direction: 'ltr',
  preload: 3,
};

/**
 * Aggregate state passed down to mode components through the Reader's
 * context-free prop drilling (we keep it explicit for clarity — there are
 * three child views, not thirty).
 */
export interface ReaderState {
  comic: ComicDetails;
  pages: PageRef[];
  currentPage: number;
  setCurrentPage: (page: number) => void;
  resolveUrl: (page: number) => Promise<string | undefined>;
  /** Tells the cache to drop the URL for `page` and re-resolve on next access. */
  invalidateUrl: (page: number) => void;
  /** Lookup the most recently-resolved URL synchronously, if any. */
  getCachedUrl: (page: number) => string | undefined;
  prefs: ReaderPrefs;
  setPrefs: (prefs: ReaderPrefs) => void;
  sourceId: string;
}
